import { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCode,
  FaCopy,
  FaDownload,
  FaEdit,
  FaExclamationCircle,
  FaFileCode,
  FaHistory,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';

import Select from 'react-select';
import toast from 'react-hot-toast';
import Pagination from '../../../../components/GenericComponents/Pagination';

import { ESTATUS_SELECT } from '../../../../catalogs/status';
import { TOAST_ERROR_IDS, TOAST_SUCCESS_IDS } from '../../../../interaces/toast.ids.interface';

import { usePrivileges } from '../../../../hooks/usePrivileges';

import { xsdApi } from '../../api/xsd.api';

import '../../../../feature-styles/xsd.css';

import { useCertificateTypes } from '../../../certificate-types/hooks/useCertificateTypes';

import NewXsdVersion from '../XsdModal/NewXsdVersion';
import XsdTableMobile from './XsdTableMobile';
import { hasSavedCertificateDesign } from '../../../CertificateBuilder/certificateStorage';
import {
  createEmptyNode,
  normalizeVersion,
  serializeNodeForPayload,
  serializeVersionForPayload,
} from '../../interfaces/xsd.interface';

/*
 * FILTER OPTIONS
 */

const typeOptions = [
  {
    value: '',
    label: 'Todos los tipos',
  },
  {
    value: 'ciudadano',
    label: 'Ciudadano',
  },
  {
    value: 'patrimonial',
    label: 'Patrimonial',
  },
  {
    value: 'empresarial',
    label: 'Empresarial',
  },
];

/*
 * 
 * FIELD AND NODE CREATION
 * 
 */



/*
 * Creates an empty version or derives one from a base version.
 */
const createEmptyVersion = (entity = null, baseVersion = null) => {
  if (baseVersion) {
    const normalized = normalizeVersion(baseVersion);
    return {
      ...normalized,
      version: numberNewVersion(entity, baseVersion.version),
      status: 'DRAFT',
      current: false,
      effectiveDate: '',
    };
  }
  return {
    version: numberNewVersion(entity) || '1.0.0',
    status: 'DRAFT',
    current: true,
    effectiveDate: '',
    targetNamespace: '',
    elementFormDefault: 'unqualified',
    attributeFormDefault: 'unqualified',
    structure: [createEmptyNode()],
  };
};

/*
 * VERSIONS
 */

const numberNewVersion = (entity, explicitBase = null) => {
  const v = explicitBase ? String(explicitBase).trim() : (lastVersion(entity)?.version ? String(lastVersion(entity).version).trim() : '1.0.0');
  const semverMatch = v.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (semverMatch) {
    const major = parseInt(semverMatch[1], 10);
    const minor = parseInt(semverMatch[2], 10);
    return `${major}.${minor + 1}.0`;
  }
  const vMatch = v.match(/_V(\d+)$/i);
  if (vMatch) {
    const base = v.replace(/_V\d+$/i, '');
    return `${base}_V${parseInt(vMatch[1], 10) + 1}`;
  }
  const numMatch = v.match(/\d+$/);
  if (numMatch) {
    const num = parseInt(numMatch[0], 10);
    const prefix = v.slice(0, numMatch.index);
    return `${prefix}${num + 1}`;
  }
  return `${v}.1`;
};

const lastVersion = (entity) => {
  if (entity && Array.isArray(entity.versions) && entity.versions.length > 0) {
    return entity.versions[entity.versions.length - 1];
  }

  return null;
};

/*
 * 
 * NORMALIZE CERTIFICATE TYPE
 * 
 */

const normalizeCertificateType = (value) => {
  const normalized = String(value || '').toLowerCase();

  if (normalized === 'ciudadano') {
    return 'citizens';
  }

  if (normalized === 'patrimonial') {
    return 'properties';
  }

  if (normalized === 'empresarial') {
    return 'businesses';
  }

  return normalized;
};

const formatCertificateType = (value) => {
  const normalized = String(value || '').toLowerCase();

  if (normalized === 'citizens' || normalized === 'ciudadano') {
    return 'Ciudadano';
  }

  if (normalized === 'properties' || normalized === 'patrimonial') {
    return 'Patrimonial';
  }

  if (normalized === 'businesses' || normalized === 'empresarial') {
    return 'Empresarial';
  }

  return value || 'Sin tipo';
};

/*
 * 
 * NORMALIZE TREE
 * 
 *
 * IMPORTANT:
 *
 * If a node has no items:
 *
 *     items: []
 *
 * Do not create a field automatically.
 */
const normalizeNodeTree = (node) => {
  if (!node || typeof node !== 'object') {
    return createEmptyNode();
  }

  return {
    ...node,

    name: node.name || '',

    items: Array.isArray(node.items)
      ? node.items.map((field) => ({
          ...field,

          itemName: field.itemName || '',

          type: field.type || '',

          regex: field.regex || '',

          regexPreset: field.regexPreset || '',

          regexPresetId: field.regexPresetId || '',

          required: Boolean(field.required),

          stringValidate: field.stringValidate || '',
        }))
      : [],

    childrens: Array.isArray(node.childrens)
      ? node.childrens.map((child) => normalizeNodeTree(child))
      : (Array.isArray(node.children) ? node.children.map((child) => normalizeNodeTree(child)) : []),
    children: Array.isArray(node.childrens)
      ? node.childrens.map((child) => normalizeNodeTree(child))
      : (Array.isArray(node.children) ? node.children.map((child) => normalizeNodeTree(child)) : []),
  };
};

/*
 * 
 * GET NODE BY PATH
 * 
 */

const getNodeAtPath = (structure, path) => {
  let currentArray = structure;

  let currentNode = null;

  for (let index = 0; index < path.length; index += 1) {
    currentNode = currentArray[path[index]];

    if (!currentNode) {
      return null;
    }

    if (index < path.length - 1) {
      currentArray = Array.isArray(currentNode.childrens)
        ? currentNode.childrens
        : (Array.isArray(currentNode.children) ? currentNode.children : []);
    }
  }

  return currentNode;
};

/*
 * 
 * GET PARENT ARRAY
 * 
 */

const getParentArrayAtPath = (structure, path) => {
  if (!path.length) {
    return structure;
  }

  let currentArray = structure;

  for (let index = 0; index < path.length - 1; index += 1) {
    const node = currentArray[path[index]];

    currentArray = Array.isArray(node?.childrens)
      ? node.childrens
      : (Array.isArray(node?.children) ? node.children : []);
  }

  return currentArray;
};

/*
 * 
 * CLONE TREE
 * 
 */

const cloneNodeTree = (value) => JSON.parse(JSON.stringify(value ?? []));

/*
 * EVENT HISTORY AND TRACEABILITY MODAL
 */
function TraceabilityModal({ xsd, onClose }) {
  if (!xsd) return null;
  const events = Array.isArray(xsd.events) ? xsd.events : [];

  const getActionBadge = (action) => {
    switch (action) {
      case 'PUBLICACION_VERSION':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'AUTO_GUARDADO_BORRADOR':
        return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'CREACION_BORRADOR':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'NUEVA_VERSION':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'ELIMINACION_VERSION':
        return 'bg-rose-100 text-rose-800 border-rose-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return String(dateStr);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
      <div className="relative flex h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary text-lg">
              <FaHistory />
            </span>
            <div>
              <h2 className="text-base font-bold text-slate-900">Historial y Trazabilidad del XSD</h2>
              <p className="text-xs text-slate-500">{xsd.name} — Registro cronológico de eventos en base de datos</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {events.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm">
              <FaHistory className="mx-auto text-3xl text-slate-300 mb-2" />
              No hay eventos registrados aún para este XSD.
            </div>
          ) : (
            <div className="relative border-l-2 border-slate-200 ml-4 space-y-5">
              {[...events].reverse().map((ev, index) => (
                <div key={index} className="relative pl-6">
                  <div className="absolute -left-2 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-primary shadow-xs" />
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className={`rounded-md border px-2 py-0.5 text-xs font-bold ${getActionBadge(ev.accion)}`}>
                        {ev.accion}
                      </span>
                      <span className="text-xs text-slate-400 font-mono flex items-center gap-1">
                        <FaClock className="text-[10px]" />
                        {formatDate(ev.fecha)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 font-medium leading-relaxed">{ev.descripcion}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-3 flex justify-between items-center text-xs text-slate-500">
          <span>Total de eventos persistidos en base de datos: <strong>{events.length}</strong></span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/*
 * 
 * COMPONENT
 * 
 */

function XsdTable({ certificateTypeId = null }) {
  const navigate = useNavigate();
  const location = useLocation();

  const openCertificateBuilder = (entity) => {
    const query = new URLSearchParams({
      xsdId: String(entity.id),
      xsdName: entity.name || '',
    });
    navigate(`/certificate-builder?${query.toString()}`, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
        returnTab: 'XSD',
      },
    });
  };

  /*
  * CERTIFICATE TYPES
   */

  const {
    namesInactives:certificateTypes,
    loading: loadingCertificateTypes,
    getNamesInactives:fetchCertificateTypes,
  } = useCertificateTypes();


  /*
  * STATE
   */

  const [xsds, setXsds] = useState([]);

  const [filtered, setFiltered] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');

  const [filterStatus, setFilterStatus] = useState('');

  const [filterType, setFilterType] = useState('');

  const [expandedIds, setExpandedIds] = useState({});

  const [creating, setCreating] = useState(false);

  const [createForm, setCreateForm] = useState({
    name: '',
    certificateTypeId: '',
    version: createEmptyVersion(),
  });

  const [newVersionFor, setNewVersionFor] = useState(null);

  const [newVersionDraft, setNewVersionDraft] = useState(createEmptyVersion());

  const [previousVersion, setPreviousVersion] = useState(null);

  const [historyModalXsd, setHistoryModalXsd] = useState(null);

  const [expandedVersionIds, setExpandedVersionIds] = useState({});

  const [pageData, setPageData] = useState({ page: 1, size: 10 });

  const [dataPagination, setDataPagination] = useState({
    first: true,
    last: false,
    page: 1,
    size: 10,
    totalElements: 0,
    totalPages: 1,
  });

  // XSD XML preview modal.
  const [xmlModal, setXmlModal] = useState({ open: false, title: '', xml: '', loading: false });
  const [copied, setCopied] = useState(false);

  /*
   * PRIVILEGIOS
   */

  const { hasPrivilege } = usePrivileges();

  /*
  * CREATE AN XSD WHEN A CERTIFICATE TYPE ID IS PROVIDED
   */

  useEffect(() => {
    if (certificateTypeId != null) {
      newXsd();
    }
  }, [certificateTypeId]);

  /*
  * UPDATE CERTIFICATE TYPE DATA
   */

  useEffect(() => {
    if (!certificateTypes) {
      return;
    }

    const certificateToAssociate = certificateTypes.find(
      (option) => option.id == certificateTypeId
    );

    setCreateForm((prev) => ({
      ...prev,

      certificateTypeId: certificateTypeId,

      name: certificateToAssociate?.name || '',

      version: {
        ...prev.version,

        version: certificateToAssociate?.name ? certificateToAssociate.name + '_V1' : '_V1',
      },
    }));
  }, [certificateTypes, certificateTypeId]);

  /*
  * FETCH XSD RECORDS
   */

  const fetchXsds = useCallback(async () => {
    setLoading(true);

    try {
      const filters = [];

      if (certificateTypeId != null) {
        filters.push({
          filter: 'typeCertificateId',
          value_filter: certificateTypeId,
        });
      }

      if (filterStatus !== '') {
        filters.push({
          filter: 'typeCertificateActive',
          value_filter: filterStatus,
        });
      }

      const response = await xsdApi.search({
        page: pageData.page,
        size: pageData.size,
        ...(search.trim() ? { search: { value: search.trim() } } : {}),
        ...(filters.length > 0 ? { filters } : {}),
      });

      setDataPagination({
        first: response.first,
        last: response.last,
        page: response.page,
        size: response.size,
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      });

      setXsds(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error.message || TOAST_ERROR_IDS.SERVER_CONNECTION_ERROR, {
        id: TOAST_ERROR_IDS.SERVER_CONNECTION_ERROR,
      });

      setXsds([]);
      setDataPagination((previous) => ({ ...previous, totalElements: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [certificateTypeId, filterStatus, pageData, search]);

  useEffect(() => {
    fetchXsds();
  }, [fetchXsds]);

  const resetToFirstPage = () => {
    setPageData((previous) => ({ ...previous, page: 1 }));
  };

  /*
  * EXPAND XSD
   */

  const toggleExpand = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /*
  * EXPAND VERSION
   */

  const toggleVersionExpand = (xsdId, versionKey) => {
    const key = `${xsdId}-${versionKey}`;

    setExpandedVersionIds((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  /*
  * RENDER NODE DETAILS
   */

  const renderNodeDetails = (node, path = [], parentName = null) => {
    const fields = Array.isArray(node?.items) ? node.items : (Array.isArray(node?.campos) ? node.campos : []);
    const children = Array.isArray(node?.childrens)
      ? node.childrens
      : (Array.isArray(node?.children)
        ? node.children
        : (Array.isArray(node?.nodos) ? node.nodos : []));
    const childNames = children.length > 0 ? children.map((child) => child?.name || 'Sin nombre') : [];
    const constraints = Array.isArray(node?.constraints) ? node.constraints : [];

    return (
      <div key={`node-${path.join('-') || 'root'}`} className="xsd-detail-node">
        <div className="xsd-detail-node-header flex items-center justify-between">
          <div className="xsd-node-identity flex items-center gap-2">
            <strong>{node?.name || 'Sin nombre'}</strong>
            <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-800">
              {node?.compositor || 'sequence'}
            </span>
            <span className="text-[10px] text-slate-500">
              [{node?.minOccurs ?? 1}..{node?.maxOccurs ?? '1'}]
            </span>
            {node?.contentModel && node.contentModel !== 'default' && (
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-700">
                {node.contentModel}
              </span>
            )}
          </div>
          {constraints.length > 0 && (
            <div className="flex gap-1">
              {constraints.map((c, ci) => (
                <span key={ci} className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                  {c.kind}: {c.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {children.length > 0 && (
          <div className="xsd-node-relations">
            <span className="xsd-node-relation-label">Hijos:</span>
            <div className="xsd-node-relation-chips">
              {childNames.map((childName, childIndex) => (
                <span
                  key={`${node?.name || 'node'}-child-${childName}-${childIndex}`}
                  className="xsd-node-chip"
                >
                  {childName}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="xsd-detail-fields">
          {fields.map((field, fieldIndex) => {
            const isAttr = field.nodeKind === 'ATTRIBUTE';
            const reqLabel = isAttr
              ? (field.use === 'required' ? 'Requerido' : 'Opcional')
              : (field.minOccurs === 0 ? 'Opcional' : 'Requerido');

            return (
              <div key={`${path.join('-')}-field-${fieldIndex}`} className="xsd-detail-field">
                <div className="flex items-center gap-1.5">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                    isAttr ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
                  }`}>
                    {isAttr ? '@Attr' : '<Elem>'}
                  </span>
                  <span><strong>{field.itemName || 'Sin nombre'}</strong></span>
                </div>

                <div>
                  <label>Tipo:</label>
                  <small className="font-mono text-[11px] text-slate-700">xs:{field.type || 'string'}</small>
                </div>

                {(field.pattern || field.regex) && (
                  <div>
                    <label>Patrón:</label>
                    <small className="font-mono text-[11px] text-slate-600 truncate max-w-[140px] block" title={field.pattern || field.regex}>
                      {field.pattern || field.regex}
                    </small>
                  </div>
                )}

                {field.length != null && (
                  <div>
                    <label>Longitud:</label>
                    <small>{field.length}</small>
                  </div>
                )}

                {(field.minLength != null || field.maxLength != null) && (
                  <div>
                    <label>Rango long.:</label>
                    <small>[{field.minLength ?? 0}..{field.maxLength ?? '∞'}]</small>
                  </div>
                )}

                {(field.enumValues || []).length > 0 && (
                  <div>
                    <label>Valores:</label>
                    <small className="truncate max-w-[120px] block" title={(field.enumValues || []).join(', ')}>
                      {(field.enumValues || []).join(', ')}
                    </small>
                  </div>
                )}

                <div>
                  <label>Estatus:</label>
                  <small className={reqLabel === 'Requerido' ? 'font-semibold text-emerald-700' : 'text-slate-500'}>
                    {reqLabel}
                  </small>
                </div>
              </div>
            );
          })}
        </div>

        {children.length > 0 && (
          <div className="xsd-detail-childs">
            {children.map((childNode, childIndex) =>
              renderNodeDetails(childNode, [...path, childIndex], node?.name || 'Sin nombre')
            )}
          </div>
        )}
      </div>
    );
  };

  /*
  * SAVE XSD
   */

  const saveXsd = async () => {
    if (!createForm.certificateTypeId) {
      toast.error('Selecciona un tipo de certificado', {
        id: 'xsd-type-required',
      });

      return;
    }

    if (!createForm.version.version?.trim()) {
      toast.error('La versión es obligatoria', {
        id: 'xsd-version-required',
      });

      return;
    }

    const payload = {
      typeCertificatesId: createForm.certificateTypeId,
      name: (createForm.name || '').trim(),
      versions: [
        {
          version: createForm.version.version.trim(),
          current: true,
          targetNamespace: createForm.version.targetNamespace || '',
          elementFormDefault: createForm.version.elementFormDefault || 'unqualified',
          attributeFormDefault: createForm.version.attributeFormDefault || 'unqualified',
          structure: (createForm.version.structure || []).map(serializeNodeForPayload),
        },
      ],
    };

    try {
      await xsdApi.create(payload);

      toast.success(TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY, {
        id: TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY,
      });

      setCreating(false);

      setCreateForm({
        name: '',
        certificateTypeId: '',
        version: createEmptyVersion(),
      });

      fetchXsds();
    } catch (error) {
      toast.error(error.message || TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION, {
        id: TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION,
      });
    }
  };

  /*
  * DRAFT AUTO-SAVE (DATABASE AND TRACEABILITY)
   */
  const handleAutoSaveDraft = async (xsdId, draft) => {
    if (!draft?.version?.trim()) return;

    const payload = {
      version: draft.version.trim(),
      status: 'DRAFT',
      current: Boolean(draft.current),
      effectiveDate: draft.effectiveDate || null,
      targetNamespace: draft.targetNamespace || '',
      elementFormDefault: draft.elementFormDefault || 'unqualified',
      attributeFormDefault: draft.attributeFormDefault || 'unqualified',
      structure: (draft.structure || []).map(serializeNodeForPayload),
    };

    try {
      const response = await xsdApi.update(xsdId, payload);
      const resData = response?.data || response;
      if (resData?.id) {
        setXsds((prev) =>
          prev.map((item) => (item.id === xsdId ? { ...item, ...resData } : item))
        );
      }
    } catch (err) {
      console.warn('Auto-save warning:', err);
    }
  };

  /*
  * SAVE OR PUBLISH AN XSD VERSION
   */
  const saveVersionToXsd = async (xsdRecord, draftToSave = null) => {
    const targetDraft = draftToSave || newVersionDraft;
    if (!targetDraft?.version?.trim()) {
      toast.error('La versión es obligatoria', {
        id: 'xsd-version-required',
      });
      return;
    }

    const isDraft = targetDraft.status === 'DRAFT';

    const payload = {
      version: targetDraft.version.trim(),
      status: targetDraft.status || 'PUBLISHED',
      current: Boolean(targetDraft.current),
      effectiveDate: targetDraft.effectiveDate || null,
      targetNamespace: targetDraft.targetNamespace || '',
      elementFormDefault: targetDraft.elementFormDefault || 'unqualified',
      attributeFormDefault: targetDraft.attributeFormDefault || 'unqualified',
      structure: (targetDraft.structure || []).map(serializeNodeForPayload),
    };

    try {
      await xsdApi.update(xsdRecord.id, payload);

      toast.success(
        isDraft
          ? `Borrador de versión ${targetDraft.version} guardado exitosamente`
          : `Versión ${targetDraft.version} validada y publicada exitosamente`
      );

      setNewVersionFor(null);
      setPreviousVersion(null);
      setNewVersionDraft(createEmptyVersion());
      fetchXsds();
    } catch (error) {
      toast.error(error.message || TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION);
    }
  };

  /*
  * VIEW GENERATED XSD XML
   */

  const handleViewXml = async (entity, version) => {
    const vKey = version.version || version;
    setXmlModal({
      open: true,
      title: `${entity.name} — Versión ${vKey}`,
      xml: '',
      loading: true,
    });

    try {
      const xml = await xsdApi.viewXsd(entity.id, vKey);
      setXmlModal((prev) => ({ ...prev, xml, loading: false }));
    } catch (error) {
      toast.error('Error al generar XML del esquema XSD: ' + (error.message || 'Error del servidor'));
      setXmlModal({ open: false, title: '', xml: '', loading: false });
    }
  };

  const handleDownloadXml = (title, xml) => {
    if (!xml) return;
    const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/[^a-z0-9]/g, '_')}.xsd`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyXml = (xml) => {
    if (!xml) return;
    navigator.clipboard.writeText(xml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('XML copiado al portapapeles');
  };

  /*
  * DELETE VERSION
   */

  const handleDeleteVersion = async (entity, versionKey) => {
    if (!window.confirm(`¿Estás seguro de eliminar la versión "${versionKey}" de ${entity.name}?`)) {
      return;
    }

    try {
      await xsdApi.deleteVersion(entity.id, versionKey);
      toast.success(`Versión "${versionKey}" eliminada correctamente`);
      fetchXsds();
    } catch (error) {
      toast.error(error.message || 'Error al eliminar versión');
    }
  };

  /*
   * SET VERSION DRAFT
   */

  const setCreateVersionDraft = (updater) => {
    setCreateForm((prev) => ({
      ...prev,

      version: typeof updater === 'function' ? updater(prev.version) : updater,
    }));
  };

  /*
  * CREATE XSD
   */

  const newXsd = () => {
    setCreating(true);

    fetchCertificateTypes();
  };

  /*
   * COMPUTED METRICS & FILTERING
   */

  const displayedXsds = xsds.filter((item) => {
    if (search && !item.name?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    if (filterStatus !== '') {
      const matchStatus = filterStatus === '1' || filterStatus === 1 || filterStatus === true;
      if (Boolean(item.status) !== matchStatus) return false;
    }
    if (filterType && filterType !== '') {
      const typeNorm = normalizeCertificateType(filterType);
      const itemNorm = String(item.name || '').toLowerCase();
      if (!itemNorm.includes(typeNorm) && !itemNorm.includes(filterType.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const totalXsdCount = xsds.length;
  const activeXsdCount = xsds.filter((x) => x.status).length;
  const draftList = xsds.flatMap((entity) => {
    const drafts = (entity.versions || []).filter((v) => v.status === 'DRAFT');
    return drafts.map((d) => ({ entity, draft: d }));
  });
  const scheduledCount = xsds.reduce((acc, entity) => {
    const sched = (entity.versions || []).filter(
      (v) => v.effectiveDate && new Date(v.effectiveDate) > new Date()
    );
    return acc + sched.length;
  }, 0);

  const openDraft = (entity, draft) => {
    setNewVersionFor(entity.id);
    setNewVersionDraft(normalizeVersion(draft));
    setPreviousVersion(null);
  };

  const createVersion = (entity, baseVersion) => {
    setNewVersionFor(entity.id);
    setNewVersionDraft(createEmptyVersion(entity, baseVersion));
    setPreviousVersion(baseVersion || null);
  };

  /*
   * RETURN
   */

  return (
    <div className="xsd-card">
      {/* 
          KPI SUMMARY CARDS (IDENTIFICACIÓN RÁPIDA DE MÓDULOS Y ESTADOS)
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600 text-lg">
            <FaLayerGroup />
          </div>
          <div>
            <div className="text-2xl font-black text-slate-800 tracking-tight">{totalXsdCount}</div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Esquemas XSD</div>
          </div>
        </div>

        <div className="flex items-center gap-3.5 p-4 rounded-2xl bg-white border border-slate-200/80 shadow-xs">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 text-lg">
            <FaCheck />
          </div>
          <div>
            <div className="text-2xl font-black text-emerald-700 tracking-tight">{activeXsdCount}</div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Versiones Activas</div>
          </div>
        </div>

        <div className={`flex items-center gap-3.5 p-4 rounded-2xl border shadow-xs transition ${
          draftList.length > 0 ? 'bg-amber-50/40 border-amber-300' : 'bg-white border-slate-200/80'
        }`}>
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl text-lg ${
            draftList.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'
          }`}>
            <FaEdit />
          </div>
          <div>
            <div className={`text-2xl font-black tracking-tight ${draftList.length > 0 ? 'text-amber-800' : 'text-slate-800'}`}>
              {draftList.length}
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Borradores en Curso</div>
          </div>
        </div>

        <div className={`flex items-center gap-3.5 p-4 rounded-2xl border shadow-xs transition ${
          scheduledCount > 0 ? 'bg-indigo-50/40 border-indigo-300' : 'bg-white border-slate-200/80'
        }`}>
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl text-lg ${
            scheduledCount > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'
          }`}>
            <FaCalendarAlt />
          </div>
          <div>
            <div className={`text-2xl font-black tracking-tight ${scheduledCount > 0 ? 'text-indigo-800' : 'text-slate-800'}`}>
              {scheduledCount}
            </div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Vigencias Programadas</div>
          </div>
        </div>
      </div>

      {/* 
          BANNER DE BORRADORES ACTIVOS CON ACCESO DIRECTO DE 1 CLIC
      */}
      {draftList.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50/30 p-4 shadow-xs">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-200 text-amber-900 text-sm shadow-xs">
              <FaEdit />
            </span>
            <div>
              <div className="font-bold text-sm text-amber-950">
                Tienes {draftList.length} borrador{draftList.length > 1 ? 'es' : ''} de esquema en edición
              </div>
              <div className="text-xs text-amber-800/80">
                Los cambios se guardan automáticamente en borrador y no alteran los certificados en producción hasta su publicación.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {draftList.map(({ entity, draft }) => (
              <button
                key={`${entity.id}-${draft.version}`}
                type="button"
                onClick={() => {
                  setNewVersionFor(entity.id);
                  setNewVersionDraft(normalizeVersion(draft));
                  setPreviousVersion(null);
                }}
                className="flex items-center gap-1.5 rounded-xl border border-amber-400 bg-white px-3.5 py-2 text-xs font-bold text-amber-900 shadow-xs hover:bg-amber-100 transition active:scale-95 cursor-pointer"
              >
                <FaEdit className="text-amber-600 text-xs" />
                <span>Continuar <strong>{entity.name}</strong> ({draft.version})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 
          TOOLBAR
      */}

      <div className="xsd-toolbar">
        <div className="xsd-toolbar-filters">
          <div className="filter-select-wrapper-sm">
            <Select
              options={typeOptions}

              value={typeOptions.find((option) => option.value === filterType) || typeOptions[0]}

              onChange={(option) => setFilterType(option?.value || '')}

              placeholder="Tipo de certificado"

              isSearchable={false}
            />
          </div>

          <div className="filter-select-wrapper-sm">
            <Select
              options={ESTATUS_SELECT}

              getOptionLabel={(option) => option.name}

              getOptionValue={(option) => option.value}

              value={ESTATUS_SELECT.find((option) => option.value === filterStatus) || null}

              onChange={(option) => {
                setFilterStatus(option?.value || '');
                resetToFirstPage();
              }}

              placeholder="Todos los estatus"

              isSearchable={false}
            />
          </div>

          <div className="search-box xsd-search">
            <FaSearch />

            <input
              type="text"
              placeholder="Buscar XSD..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetToFirstPage();
              }}
            />
          </div>
        </div>

        {hasPrivilege('MOD_CONFIGURATION_SYSTEM:CREATE') && (
          <button
            type="button"
            className="btn-create-secondary w-fit text-sm"
            onClick={() => newXsd()}
          >
            <FaPlus />
            Crear XSD
          </button>
        )}
      </div>

      {/* 
          TABLA
      */}

      <div className="xsd-results-area">
        <div className="xsd-desktop-table">
          <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>NOMBRE DEL ESQUEMA XSD</th>

              <th>ESTADO Y VERSIONES</th>

              <th>ACCIONES RÁPIDAS</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="3" className="td-empty">
                  Cargando esquemas XSD...
                </td>
              </tr>
            ) : displayedXsds.length === 0 ? (
              <tr>
                <td colSpan="3" className="td-empty">
                  No se encontraron esquemas XSD coincidentes.
                </td>
              </tr>
            ) : (
              displayedXsds.map((entity) => {
                const entityDraft = (entity.versions || []).find((v) => v.status === 'DRAFT');
                const hasCertificateDesign = hasSavedCertificateDesign(entity.id);
                const hasScheduled = (entity.versions || []).some(
                  (v) => v.effectiveDate && new Date(v.effectiveDate) > new Date()
                );

                return (
                  <Fragment key={entity.id}>
                    {/* 
                          FILA XSD
                      */}

                    <tr className="xsd-row hover:bg-slate-50/70 transition">
                      <td className="font-semibold text-slate-800">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600 text-sm border border-teal-200/60 shadow-2xs">
                            <FaFileCode />
                          </span>
                          <div>
                            <div className="font-bold text-sm text-slate-900">{entity.name}</div>
                            <div className="text-[11px] text-slate-500 font-medium">
                              {(entity.versions || []).length} versión{(entity.versions || []).length !== 1 ? 'es' : ''} registrada{(entity.versions || []).length !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-bold ${
                              entity.status
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {entity.activeVersion ? `${entity.activeVersion} (Vigente)` : '—'}
                          </span>

                          {entityDraft && (
                            <span className="rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-[11px] font-bold text-amber-800 flex items-center gap-1.5">
                              <FaEdit className="text-[10px]" />
                              <span>Borrador ({entityDraft.version})</span>
                            </span>
                          )}

                          {hasScheduled && (
                            <span className="rounded-full bg-indigo-100 border border-indigo-300 px-2.5 py-0.5 text-[11px] font-bold text-indigo-800 flex items-center gap-1.5">
                              <FaClock className="text-[10px]" />
                              <span>Programada</span>
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            className="flex items-center gap-1.5 rounded-xl border border-teal-300 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 shadow-xs hover:bg-teal-100 transition active:scale-95 cursor-pointer"
                            onClick={() => openCertificateBuilder(entity)}
                            title={hasCertificateDesign ? 'Editar el diseño guardado para este XSD' : 'Crear un diseño de certificado para este XSD'}
                          >
                            <FaFileCode className="text-xs" />
                            {hasCertificateDesign ? 'Editar certificado' : 'Generar certificado'}
                          </button>
                          {entityDraft ? (
                            <button
                              type="button"
                              className="flex items-center gap-1.5 rounded-xl border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 shadow-xs hover:bg-amber-100 transition active:scale-95 cursor-pointer"
                              onClick={() => {
                                setNewVersionFor(entity.id);
                                setNewVersionDraft(normalizeVersion(entityDraft));
                                setPreviousVersion(null);
                              }}
                              title="Continuar editando el borrador"
                            >
                              <FaEdit className="text-amber-600 text-xs" />
                              Editar borrador
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="flex items-center gap-1.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary shadow-xs hover:bg-primary/10 transition active:scale-95 cursor-pointer"
                              onClick={() => {
                                const lastV = lastVersion(entity);
                                setNewVersionFor(entity.id);
                                setNewVersionDraft(createEmptyVersion(entity, lastV));
                                setPreviousVersion(lastV);
                              }}
                              title="Crear una nueva versión de este XSD"
                            >
                              <FaPlus className="text-xs" />
                              Nueva versión
                            </button>
                          )}

                          <button
                            type="button"
                            className="flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-xs hover:bg-slate-50 transition cursor-pointer"
                            onClick={() => setHistoryModalXsd(entity)}
                            title="Ver historial de eventos y auditoría"
                          >
                            <FaHistory className="text-primary text-xs" />
                            Trazabilidad ({(entity.events || []).length})
                          </button>

                          <button
                            type="button"
                            className="xsd-btn-secondary xsd-btn-mini"
                            onClick={() => toggleExpand(entity.id)}
                            title={expandedIds[entity.id] ? 'Contraer versiones' : 'Expandir versiones'}
                          >
                            {expandedIds[entity.id] ? <FaChevronUp /> : <FaChevronDown />}
                          </button>
                        </div>
                      </td>
                    </tr>

                  {/* 
                        DETALLE XSD
                    */}

                  {expandedIds[entity.id] && (
                    <tr>
                      <td colSpan="3" className="xsd-expanded-cell">
                        <div className="xsd-expanded-details">
                          <div className="xsd-detail-header flex items-center justify-between">
                            <strong>Versiones y estructura</strong>
                            <button
                              type="button"
                              className="flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:bg-slate-50 transition"
                              onClick={() => setHistoryModalXsd(entity)}
                              title="Ver bitácora y trazabilidad de cambios en base de datos"
                            >
                              <FaHistory className="text-primary text-xs" />
                              Historial y Trazabilidad ({(Array.isArray(entity.events) ? entity.events : []).length})
                            </button>
                          </div>

                          {(Array.isArray(entity.versions) ? entity.versions : []).map(
                            (version, index) => {
                              const versionKey = version.version || `v${index + 1}`;
                              const expanded = expandedVersionIds[`${entity.id}-${versionKey}`];
                              const isDraft = version.status === 'DRAFT';
                              const isScheduled = version.effectiveDate && new Date(version.effectiveDate) > new Date();

                              return (
                                <div
                                  key={`${entity.id}-version-${versionKey}`}
                                  className={`xsd-version-box xsd-compact-version-card ${
                                    isDraft ? 'border-amber-300 bg-amber-50/20' : ''
                                  }`}
                                >
                                  {/* 
                                        HEADER VERSION
                                    */}

                                  <div className="xsd-version-summary-header">
                                    <div className="xsd-version-summary-main flex items-center gap-2">
                                      {version.version && <strong>{version.version}</strong>}

                                      <span className="text-slate-500 text-xs">
                                        ({Array.isArray(version.structure)
                                          ? version.structure.length
                                          : 0}{' '}
                                        nodos)
                                      </span>

                                      {isDraft ? (
                                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                                          <FaEdit className="text-[10px]" />
                                          <span>Borrador</span>
                                        </span>
                                      ) : isScheduled ? (
                                        <span
                                          className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-indigo-100 px-2 py-0.5 text-[11px] font-bold text-indigo-800"
                                          title={`Entrada en vigor: ${new Date(version.effectiveDate).toLocaleString()}`}
                                        >
                                          <FaClock className="text-[10px]" />
                                          <span>Programada ({new Date(version.effectiveDate).toLocaleDateString()})</span>
                                        </span>
                                      ) : version.current ? (
                                        <span className="xsd-version-badge">Actual / Vigente</span>
                                      ) : (
                                        <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                                          Histórica
                                        </span>
                                      )}
                                    </div>

                                    <div className="xsd-version-summary-actions flex flex-wrap items-center gap-2">
                                      {/* Drafts can be opened directly for editing. */}
                                      {isDraft ? (
                                        <button
                                          type="button"
                                          className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 transition hover:bg-amber-100"
                                          onClick={() => {
                                            setNewVersionFor(entity.id);
                                            setNewVersionDraft(normalizeVersion(version));
                                            setPreviousVersion(null);
                                          }}
                                          title="Continuar editando este borrador"
                                        >
                                          <FaEdit className="text-[11px]" />
                                          Editar borrador
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-semibold text-primary transition hover:bg-primary/10"
                                          onClick={() => {
                                            setNewVersionFor(entity.id);
                                            setNewVersionDraft(createEmptyVersion(entity, version));
                                            setPreviousVersion(version);
                                          }}
                                          title="Generar una nueva versión basada en esta estructura"
                                        >
                                          <FaPlus className="text-[10px]" />
                                          Nueva versión desde esta
                                        </button>
                                      )}

                                      <button
                                        type="button"
                                        className="flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 transition hover:bg-teal-100"
                                        onClick={() => handleViewXml(entity, version)}
                                        title="Ver XML generado del esquema XSD"
                                      >
                                        <FaCode className="text-[11px]" />
                                        Ver XML
                                      </button>

                                      {(entity.versions || []).length > 1 && (
                                        <button
                                          type="button"
                                          className="flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 p-1.5 text-xs text-red-600 transition hover:bg-red-100"
                                          onClick={() => handleDeleteVersion(entity, versionKey)}
                                          title="Eliminar esta versión"
                                        >
                                          <FaTrash className="text-[10px]" />
                                        </button>
                                      )}

                                      <button
                                        type="button"
                                        className="xsd-btn-secondary xsd-btn-mini"
                                        onClick={() => toggleVersionExpand(entity.id, versionKey)}
                                      >
                                        {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                      </button>
                                    </div>
                                  </div>

                                  {/* 
                                        META VERSION
                                    */}

                                  {!expanded && (
                                    <div className="xsd-version-summary-meta">
                                      <span>{version.version || `Versión ${index + 1}`}</span>

                                      <span>
                                        {isDraft
                                          ? 'Borrador en edición'
                                          : isScheduled
                                          ? `Entra en vigor el: ${new Date(version.effectiveDate).toLocaleString()}`
                                          : version.current
                                          ? 'Activa'
                                          : 'Inactiva'}
                                      </span>
                                    </div>
                                  )}

                                  {/* 
                                        DETALLE VERSION
                                    */}

                                  {expanded && (
                                    <div className="xsd-version-detail-panel">
                                      {Array.isArray(version.structure) &&
                                      version.structure.length > 0 ? (
                                        version.structure.map((node, nodeIndex) =>
                                          renderNodeDetails(node, [nodeIndex])
                                        )
                                      ) : (
                                        <div className="xsd-empty-state">
                                          Sin estructura definida.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          )}

                          {/* 
                                AGREGAR VERSION
                            */}

                          <button
                            type="button"
                            className="xsd-add-version-btn"
                            onClick={() => {
                              const lastV = lastVersion(entity);
                              setNewVersionFor(entity.id);
                              setNewVersionDraft(createEmptyVersion(entity, lastV));
                              setPreviousVersion(lastV);
                            }}
                          >
                            <FaPlus />
                            Agregar versión
                          </button>

                          {/* FIN VERSIONES */}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
            )}
          </tbody>
        </table>
          </div>
        </div>

        <XsdTableMobile
          xsds={displayedXsds}
          loading={loading}
          expandedIds={expandedIds}
          expandedVersionIds={expandedVersionIds}
          onToggleExpand={toggleExpand}
          onToggleVersionExpand={toggleVersionExpand}
          onEditDraft={openDraft}
          onCreateVersion={createVersion}
          onAddVersion={(entity) => createVersion(entity, lastVersion(entity))}
          onShowHistory={setHistoryModalXsd}
          onViewXml={handleViewXml}
          onDeleteVersion={handleDeleteVersion}
          onGenerateCertificate={openCertificateBuilder}
          hasSavedCertificateDesign={hasSavedCertificateDesign}
          renderNodeDetails={renderNodeDetails}
        />

      </div>

      <div className="xsd-pagination-card">
        <Pagination
          totalPage={Math.max(dataPagination.totalPages, 1)}
          currentPage={pageData.page}
          setCurrentPage={(page) => setPageData((previous) => ({ ...previous, page }))}
        />
      </div>

      {/* 
          MODAL WORKSPACE: NUEVA VERSIÓN O EDICIÓN DE BORRADOR
      */}
      {newVersionFor && (
        <div className="fixed inset-0 z-[9999] flex flex-col bg-slate-950/70 backdrop-blur-xs p-2 sm:p-5 animate-fade-in">
          <div className="relative flex flex-col h-full w-full overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200">
            <NewXsdVersion
              draft={newVersionDraft}
              setDraft={setNewVersionDraft}
              onSave={(draftPayload) => {
                const entity = xsds.find((x) => x.id === newVersionFor);
                if (entity) saveVersionToXsd(entity, draftPayload);
              }}
              saveLabel="Publicar versión"
              extraForm={null}
              onClose={() => {
                setNewVersionFor(null);
                setPreviousVersion(null);
              }}
              previousVersion={previousVersion}
              onAutoSave={(draftPayload) => handleAutoSaveDraft(newVersionFor, draftPayload)}
              isDraftMode={true}
              xsdEvents={xsds.find((x) => x.id === newVersionFor)?.events || []}
              entityName={xsds.find((x) => x.id === newVersionFor)?.name || ''}
            />
          </div>
        </div>
      )}

      {/* 
          MODAL WORKSPACE: CREAR NUEVO XSD DESDE CERO
      */}
      {creating && (
        <div className="fixed inset-0 z-[9999] flex flex-col bg-slate-950/70 backdrop-blur-xs p-2 sm:p-5 animate-fade-in">
          <div className="relative flex flex-col h-full w-full overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200">
            <NewXsdVersion
              draft={createForm.version}
              setDraft={setCreateVersionDraft}
              onSave={saveXsd}
              saveLabel="Guardar y Registrar XSD"
              extraForm={
                <div className="border-b border-teal-200 bg-teal-50/80 px-6 py-4">
                  <div className="flex items-center gap-2 mb-2 font-bold text-xs uppercase tracking-wider text-teal-900">
                    <span>Vinculación Inicial con Tipo de Certificado</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="xsd-field">
                      <label className="xsd-label font-semibold text-xs text-slate-700">Tipo de certificado disponible</label>
                      <Select
                        options={(certificateTypes || [])
                          .map((type) => ({
                            value: type.id,
                            label: type.name,
                            id: type.id,
                          }))}
                        value={
                          (certificateTypes || [])
                            .map((type) => ({
                              value: type.id,
                              label: type.name,
                              id: type.id,
                            }))
                            .find((option) => option.value === createForm.certificateTypeId) || null
                        }
                        onChange={(option) => {
                          if (!option) {
                            setCreateForm((prev) => ({
                              ...prev,
                              certificateTypeId: '',
                              name: '',
                              version: { ...prev.version, version: '' },
                            }));
                            return;
                          }
                          setCreateForm((prev) => ({
                            ...prev,
                            certificateTypeId: option.value,
                            name: 'XSD - ' + option.label,
                            version: { ...prev.version, version: option.label + '_V1' },
                          }));
                        }}
                        placeholder="Selecciona un tipo de certificado..."
                        isSearchable={false}
                      />
                    </div>
                    <div className="xsd-field">
                      <label className="xsd-label font-semibold text-xs text-slate-700">Nombre del Esquema XSD</label>
                      <input
                        type="text"
                        className="xsd-input bg-white"
                        value={createForm.name}
                        onChange={(e) =>
                          setCreateForm((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Ej. XSD - Certificado de Nacimiento"
                      />
                    </div>
                  </div>
                </div>
              }
              onClose={() => setCreating(false)}
              isDraftMode={false}
              entityName={createForm.name || 'Nuevo Esquema XSD'}
            />
          </div>
        </div>
      )}

      {/* 
          XSD XML PREVIEW MODAL
      */}
      {xmlModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="relative flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-teal-50 to-white px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                  <FaFileCode className="text-base" />
                </span>
                <div>
                  <h2 className="text-base font-bold text-slate-900">{xmlModal.title}</h2>
                  <p className="text-xs text-slate-500">Esquema W3C XML Schema (XSD 1.0)</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleCopyXml(xmlModal.xml)}
                  className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  disabled={xmlModal.loading}
                >
                  {copied ? <FaCheck className="text-emerald-500 text-xs" /> : <FaCopy className="text-slate-500 text-xs" />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadXml(xmlModal.title, xmlModal.xml)}
                  className="flex items-center gap-1.5 rounded-xl bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:bg-teal-700"
                  disabled={xmlModal.loading}
                >
                  <FaDownload className="text-xs" />
                  Descargar .xsd
                </button>
                <button
                  type="button"
                  onClick={() => setXmlModal({ open: false, title: '', xml: '', loading: false })}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 ml-2"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-900 p-6 text-xs font-mono text-emerald-400">
              {xmlModal.loading ? (
                <div className="flex h-full items-center justify-center text-slate-400">
                  Generando XML del esquema...
                </div>
              ) : (
                <pre className="whitespace-pre overflow-x-auto leading-relaxed">{xmlModal.xml}</pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 
          EVENT HISTORY AND TRACEABILITY MODAL
      */}
      {historyModalXsd && (
        <TraceabilityModal
          xsd={historyModalXsd}
          onClose={() => setHistoryModalXsd(null)}
        />
      )}
    </div>
  );
}

export default XsdTable;
